import React, {Component} from 'react';
import {connect} from "react-redux";
import {withTranslation} from "react-i18next";

import employeeService from "../../../services/Employee.service";

import {
  AWARD_SUMMARY_OPTIONS,
  DETAILED_TAX_INFORMATION_OPTIONS,
  VEST_OR_TAX_OPTIONS,
  ROW_HEADER_LEFT_MARGIN,
  ROW_HEADER_TITLE_FONT_WEIGHT, ROW_HEADER_TITLE_FONT_SIZE, ROW_HEADER_INFO_FONT_SIZE
} from "./common";
import {RowActionMenu} from "./RowActionMenu";
import UnvestedAwardDetailsDialog from "../common/dialogs/UnvestedAwardDetailsDialog";
import VestedAwardDetailsDialog from "../common/dialogs/VestedAwardDetailsDialog";
import VestsArea from "./VestsArea";

import {Col, Row, View} from '@amzn/stencil-react-components/layout'
import {ExpanderContent} from "@amzn/stencil-react-components/expander";
import {TriggerButton} from "@amzn/stencil-react-components/helpers";
import {H3, Text} from "@amzn/stencil-react-components/text";

class CalendarRow extends Component {
  constructor(props) {
    super(props);

    this.state = {
      // `custom` view option doesn't have separate rows and thus displays vest tiles by default
      isExpanded: this.props.isCustom ? true : false,
      isUnvestedAwardDialogVisible: false,
      isVestedAwardDialogVisible: false
    };
  }

  onExpandButtonClick() {
    this.setState({
      isExpanded: !this.state.isExpanded
    });
  }

  onRowMenuItemClick = (key) => {
    this.props.onRowMenuItemClick(key, this.props.year);
  }

  onTransactionClick = (transaction) => {
    let award = employeeService.loadAward(this.props.awards, transaction.award_id);

    this.setState({
      isVestedAwardDialogVisible: true,
      award: award,
      transaction: transaction,
      brokers: employeeService.getBrokers(this.props.t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)
    });
  }

  onVestClick = (vest) => {
    if (!vest.vested) {
      let award = employeeService.loadAward(this.props.awards, vest.award_id);

      this.setState({
        isUnvestedAwardDialogVisible: true,
        award: award,
        vest: vest,
        brokers: employeeService.getBrokers(this.props.t, award.tax_broker.name, award.net_broker.name, award.mobile_tax_sale_in)
      });
    }
  }

  canPerformActionOnVests = (vests) => {
    return this.props.fractionalMode && vests && vests.length > 0;
  }

  shouldShowTaxRateChangeMenuOption = (remainingVests, awardsIds) => {
    if (!this.canPerformActionOnVests(remainingVests)) {
      return false;
    }

    // If there's at least one eligible award for tax rate change, show the option.
    for (let awardId of awardsIds) {
      let award = employeeService.loadAwardById(this.props.awards, awardId);
      if (employeeService.canChangeAwardTaxRate(award)) {
        return true;
      }
    }

    return false;
  }

  getRowMenuOptions(t, remainingVests) {
    let options = []
    options.push({
      value: AWARD_SUMMARY_OPTIONS.VIEW_BY_PDF,
      label: t('StockAwards.CalendarSection.CalendarVestMenu.ViewVestsPdfOption')
    });
    options.push({
      value: AWARD_SUMMARY_OPTIONS.DOWNLOAD_EXCEL,
      label: t('StockAwards.CalendarSection.CalendarVestMenu.DownloadVestsExcelOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.VIEW_BY_PDF,
      label: t('StockAwards.CalendarSection.CalendarVestMenu.ViewDetailedPdfOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.DOWNLOAD_EXCEL,
      label: t('StockAwards.CalendarSection.CalendarVestMenu.DownloadDetailedExcelOption')
    });
    options.push({
      value: DETAILED_TAX_INFORMATION_OPTIONS.VIEW_STATEMENT,
      label: t('StockAwards.CalendarSection.CalendarVestMenu.ViewStatementOption')
    });

    if (this.canPerformActionOnVests(remainingVests)) {
      options.push({
        value: VEST_OR_TAX_OPTIONS.VEST_ELECTION,
        label: t('StockAwards.CalendarSection.CalendarVestMenu.VestElection')
      });
    }

    if (this.shouldShowTaxRateChangeMenuOption(remainingVests, this.props.selectedAwardIds)) {
      options.push({
        value: VEST_OR_TAX_OPTIONS.US_FED_TAX_RATE,
        label: t('StockAwards.CalendarSection.CalendarVestMenu.USFedRate')
      });
    }

    return options;
  }

  renderRowTitle(title, rowInfoText) {
    return (
      <>
        <Row alignItems="center" gridGap="S100" flexWrap="wrap">
          <TriggerButton
            aria-label={title}
            aria-controls={title}
            iconAltText="Calendar View Vest Section Trigger Button"
            isExpanded={this.state.isExpanded}
            onClick={() => this.onExpandButtonClick()}>
          </TriggerButton>

          <H3 fontWeight={ROW_HEADER_TITLE_FONT_WEIGHT} fontSize={ROW_HEADER_TITLE_FONT_SIZE}>
              {title}
          </H3>
        </Row>

        <View padding={{left: ROW_HEADER_LEFT_MARGIN}}>
          <Text fontSize={ROW_HEADER_INFO_FONT_SIZE}>{rowInfoText}</Text>
        </View>
      </>
    )
  }

  renderRowInfo(t, remainingVests) {
    const menuOptions = this.getRowMenuOptions(t, remainingVests);

    return (
      <Row justifyContent="space-between">
        <Col width={600}>
          {this.renderRowTitle(this.props.title, this.props.subTitle)}
        </Col>
        <Col>
          <RowActionMenu
            title={this.props.title}
            menuItems={menuOptions}
            onMenuItemClick={this.onRowMenuItemClick}
          />
        </Col>
      </Row>
    )
  }

  render() {
    const {t, awards, remainingVests, completedVests, isCustom} = this.props;

    return (
      <>
        {!isCustom && this.renderRowInfo(t, remainingVests)}

        <ExpanderContent id={this.props.title} isExpanded={this.state.isExpanded}>
          <Col backgroundColor="neutral05" padding="S400">
            {completedVests && completedVests.length > 0 &&
              <VestsArea
                title={t('StockAwards.CalendarSection.CompletedVestsTitle')}
                transactions={completedVests}
                isRSU={true}
                awards={awards}
                onVestClick={this.onTransactionClick}
              />
            }

            {remainingVests && remainingVests.length > 0 &&
              <VestsArea
                title={t('StockAwards.CalendarSection.RemainingVestsTitle')}
                vests={remainingVests}
                isRSU={true}
                awards={awards}
                onVestClick={this.onVestClick}
              />
            }
          </Col>

          <VestedAwardDetailsDialog
            award={this.state.award}
            brokers={this.state.brokers}
            transaction={this.state.transaction}
            visible={this.state.isVestedAwardDialogVisible}
            onClose={() => this.setState({isVestedAwardDialogVisible: false})}
          />

          <UnvestedAwardDetailsDialog
            award={this.state.award}
            broker={this.state.award ? this.state.award.tax_broker : null}
            brokers={this.state.brokers}
            vest={this.state.vest}
            visible={this.state.isUnvestedAwardDialogVisible}
            onClose={() => this.setState({isUnvestedAwardDialogVisible: false})}
            onChangePaymentMethodClick={this.props.onChangePaymentMethodClick}
          />

        </ExpanderContent>
      </>
    )
  }
}

const mapState = state => ({
  awards: state.employee.awards,
  fractionalMode: state.static_content.fractional_mode
})

export default connect(mapState)(withTranslation('translationsV2')(CalendarRow))
